import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Paperclip, X, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_FILES = 5;
const MAX_MESSAGE_CHARS = 2000;
const WORD_TARGET = 100;

/**
 * Footer-level feedback capture. Name / email / subject / message + up to 5
 * PDF/image attachments (1MB each). Hits POST /api/feedback.
 */
export default function FeedbackForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const wordCount = message.trim() ? message.trim().split(/\s+/).length : 0;

  const onFilesPicked = (picked) => {
    const list = Array.from(picked || []);
    if (!list.length) return;
    if (files.length + list.length > MAX_FILES) {
      toast.error(`You can attach at most ${MAX_FILES} files.`);
      return;
    }
    const accepted = [];
    for (const f of list) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} is over 1MB — compress it and retry.`);
        continue;
      }
      if (!/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) {
        toast.error(`${f.name} — only JPG/PNG/WEBP/PDF allowed.`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => [...prev, ...accepted].slice(0, MAX_FILES));
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error("All fields are required.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim());
      fd.append("subject", subject.trim());
      fd.append("message", message.trim());
      for (const f of files) fd.append("files", f, f.name);
      const res = await fetch(`${API}/feedback`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data?.detail === "string" ? data.detail : "Could not submit.";
        toast.error(detail);
        return;
      }
      toast.success(data.message || "Thanks for your feedback!");
      setDone(true);
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setFiles([]);
    } catch {
      toast.error("Network error. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="feedback"
      className="border-t border-[#2A2A35] bg-[#0A0A0F]"
      data-testid="feedback-section"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-6 py-10 md:py-14">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-5 h-5 text-[#FF6B00]" />
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
            — Feedback
          </div>
        </div>
        <h2
          className="font-display uppercase text-3xl md:text-4xl leading-[1.05] mb-2"
          style={{ fontFamily: "'Pirata One', serif" }}
        >
          Tell us what to <span className="text-[#FF6B00]">build next.</span>
        </h2>
        <p className="text-sm text-[#888] mb-6 md:mb-8 leading-relaxed">
          Spotted a bug? Got a feature request? Screenshots welcome. We read every single
          note.
        </p>

        {done ? (
          <div
            className="bg-[#1D9E75]/10 border border-[#1D9E75]/40 p-6 flex items-start gap-3"
            data-testid="feedback-success"
          >
            <CheckCircle2 className="w-6 h-6 text-[#1D9E75] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-bold text-[#F0EDE8] mb-1">
                Thanks — your feedback is in.
              </div>
              <p className="text-xs text-[#888] leading-relaxed">
                We'll reply from{" "}
                <a
                  href="mailto:support@solutionist.co.in"
                  className="text-[#FF6B00] hover:text-[#F0EDE8]"
                >
                  support@solutionist.co.in
                </a>
                . Want to send another?{" "}
                <button
                  type="button"
                  onClick={() => setDone(false)}
                  className="text-[#FF6B00] hover:text-[#F0EDE8] underline"
                  data-testid="feedback-send-another-btn"
                >
                  Send another
                </button>
                .
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="feedback-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  className="cartiq-input w-full"
                  required
                  data-testid="feedback-name-input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  maxLength={200}
                  className="cartiq-input w-full"
                  required
                  data-testid="feedback-email-input"
                />
              </Field>
            </div>

            <Field label="Subject" required>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="cartiq-input w-full"
                required
                data-testid="feedback-subject-input"
              />
            </Field>

            <Field
              label="Feedback"
              hint={`${wordCount}/${WORD_TARGET} words · ${message.length}/${MAX_MESSAGE_CHARS} chars`}
              required
            >
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
                rows={6}
                className="cartiq-input w-full resize-y min-h-[120px]"
                placeholder="Share bugs, ideas, or love letters. 100 words is plenty."
                required
                data-testid="feedback-message-input"
              />
            </Field>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold">
                  Attachments · <span className="text-[#555]">Up to 5 files · 1MB each · JPG/PNG/PDF</span>
                </div>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="btn-ghost flex items-center gap-2 text-xs"
                  data-testid="feedback-attach-btn"
                  disabled={files.length >= MAX_FILES}
                >
                  <Paperclip className="w-3.5 h-3.5" /> Attach
                </button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                  multiple
                  onChange={(e) => onFilesPicked(e.target.files)}
                  className="hidden"
                  data-testid="feedback-file-input"
                />
              </div>
              {files.length > 0 && (
                <ul className="space-y-1.5" data-testid="feedback-file-list">
                  {files.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-2 bg-[#14141A] border border-[#2A2A35] px-3 py-2 text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Paperclip className="w-3.5 h-3.5 text-[#666] shrink-0" />
                        <span className="truncate text-[#F0EDE8]">{f.name}</span>
                        <span className="text-[#555] shrink-0 font-mono-num">
                          {(f.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="text-[#888] hover:text-[#E24B4A] shrink-0"
                        aria-label={`Remove ${f.name}`}
                        data-testid={`feedback-file-remove-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-saffron w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="feedback-submit-btn"
            >
              {submitting ? "Sending…" : "Send feedback →"}
            </button>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#555]">
              Replies come from support@solutionist.co.in
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#888] font-bold">
        {label}
        {required && <span className="text-[#FF6B00]"> *</span>}
      </span>
      {hint && <span className="text-[10px] text-[#555] font-mono-num">{hint}</span>}
    </div>
    {children}
  </label>
);
